// Single, shared loader for the Google Maps JavaScript API.
//
// Previously Dashboard.tsx and CompetitorInsightsMap.tsx each injected their own
// `maps/api/js` <script> and guarded only on `window.googleMapsLoaded` — a flag set in the async
// `onload`. When both mounted together (dashboard load), their effects both ran before either
// script finished loading, both saw the flag false, and both injected the script → Google's
// "included the Google Maps JavaScript API multiple times" warning (and duplicate library loads).
//
// This module dedupes via a module-level in-flight promise: concurrent callers share ONE injection,
// and the script is never removed (removing it while another consumer relies on it breaks maps and
// re-injecting re-triggers the warning). `window.googleMapsLoaded` is still set to true on load so
// existing readers keep working.

declare global {
  interface Window {
    googleMapsLoaded?: boolean
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    google?: any
  }
}

const SCRIPT_MARKER = 'data-google-maps-loader'

let loadPromise: Promise<void> | null = null

/**
 * Loads the Google Maps JS API exactly once for the app. Safe to call from multiple components
 * concurrently — they all await the same underlying load. Resolves once `window.google.maps` is
 * available; rejects (and resets, so a later call can retry) if the script fails to load.
 */
export const loadGoogleMaps = async (
  apiKey: string,
  libraries = 'places',
): Promise<void> => {
  // Already loaded in this session.
  if (typeof window !== 'undefined' && window.google?.maps) {
    window.googleMapsLoaded = true
    return
  }

  // A load is already in flight (or completed) — share it.
  if (loadPromise) {
    return await loadPromise
  }

  loadPromise = new Promise<void>((resolve, reject) => {
    // A script tag may already exist (e.g. injected by an earlier build/hot-reload); attach to it
    // rather than adding a second one.
    const existing = document.querySelector<HTMLScriptElement>(
      `script[${SCRIPT_MARKER}="true"]`,
    )
    if (existing) {
      if (window.google?.maps) {
        window.googleMapsLoaded = true
        resolve()
        return
      }
      existing.addEventListener('load', () => {
        window.googleMapsLoaded = true
        resolve()
      })
      existing.addEventListener('error', () => {
        loadPromise = null
        reject(new Error('Google Maps failed to load'))
      })
      return
    }

    const script = document.createElement('script')
    script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=${libraries}`
    script.async = true
    script.defer = true
    script.setAttribute(SCRIPT_MARKER, 'true')
    script.onload = () => {
      window.googleMapsLoaded = true
      resolve()
    }
    script.onerror = () => {
      // Allow a retry on a later call.
      loadPromise = null
      reject(new Error('Google Maps failed to load'))
    }
    document.body.appendChild(script)
  })

  return await loadPromise
}
